import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Lang = "ja" | "en";

const LANG_KEY = "pix-portal:lang";

interface I18nContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: keyof typeof MESSAGES.ja, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const v = localStorage.getItem(LANG_KEY);
      if (v === "ja" || v === "en") return v;
    } catch {
      /* ignore */
    }
    // ブラウザ言語からフォールバック推定
    if (typeof navigator !== "undefined" && navigator.language.startsWith("en"))
      return "en";
    return "ja";
  });

  useEffect(() => {
    try {
      localStorage.setItem(LANG_KEY, lang);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l: Lang) => setLangState(l), []);

  const t = useCallback<I18nContextValue["t"]>(
    (key, params) => {
      const dict = MESSAGES[lang] ?? MESSAGES.ja;
      let s = (dict[key] ?? MESSAGES.ja[key] ?? String(key)) as string;
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          s = s.replaceAll(`{${k}}`, String(v));
        }
      }
      return s;
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("I18nProvider が祖先に必要です");
  return ctx;
}

export function useT() {
  return useI18n().t;
}

// =============================================================
// メッセージ辞書
// 作品タイトル等のコンテンツは翻訳対象外(DB に格納された言語そのまま)。
// 翻訳は UI のラベルやガイド文のみ。
// =============================================================

const MESSAGES = {
  ja: {
    // ヘッダー
    "header.searchPlaceholder": "作品・作者・タグから探す",
    "header.searchAria": "検索",
    "header.genre": "ジャンル",
    "header.ranking": "ランキング",
    "header.library": "ライブラリ",
    "header.notifications": "通知",
    "header.notificationsTitle": "通知",
    "header.notificationsNewCount": "{count} 件の新着",
    "header.viewAll": "すべて見る",
    "header.profile": "プロフィール",
    "header.creator": "クリエイター",
    "header.dashboard": "作者ダッシュボード",
    "header.openInStudio": "Studio で開く",
    "header.help": "ヘルプ",
    "header.logout": "ログアウト",
    "header.login": "ログイン",
    "header.signup": "新規登録",
    "header.popularKeywords": "人気のキーワード",
    "header.workSuggestions": "作品候補",
    // モバイル
    "mobile.home": "ホーム",
    "mobile.search": "探す",
    "mobile.library": "ライブラリ",
    "mobile.profile": "プロフィール",
    // ホーム
    "home.pickup": "★ 編集部ピックアップ · WEEK {week}",
    "home.playFree": "▶ 無料でプレイ",
    "home.viewDetail": "詳細を見る",
    "home.addLibrary": "ライブラリに追加",
    "home.new": "新着",
    "home.newEn": "// new releases · 7d",
    "home.weeklyRanking": "週間ランキング",
    "home.weeklyRankingEn": "// weekly top 10",
    "home.byGenre": "ジャンルから探す",
    "home.byGenreEn": "// by genre",
    "home.viewMore": "もっと見る →",
    "home.viewAll": "すべて →",
    "home.reviews": "{count} 件のレビュー",
    "home.pickupSubtitle": "静かな夜、選択肢7つ、エンディング3種。今だけ無料公開中。",
    "home.emptyGenre": "このジャンルの作品はまだありません",
    "home.emptyGenreDesc": "あなたが書いてみますか?",
    "home.studioCreate": "Studio で作る →",
    // 作品ページ
    "work.home": "HOME",
    "work.r18Title": "R-18 コンテンツ",
    "work.r18Note": "クリックして表示する",
    "work.r18Show": "表示する",
    "work.buy": "購入する",
    "work.play": "▶ プレイ",
    "work.share": "X でシェア",
    "work.copyLink": "リンクをコピー",
    "work.copyLinkDone": "リンクをコピーしました",
    "work.purchaseDone": "「{title}」を購入しました ({price})",
    "work.addLibraryDone": "ライブラリに追加しました",
    "work.removeLibraryDone": "ライブラリから外しました",
    "work.inLibrary": "♥ ライブラリ済み",
    "work.notInLibrary": "♡ ライブラリに追加",
    "work.screenshots": "SCREENSHOTS // {count}枚",
    "work.reviews": "レビュー",
    "work.reviewsEn": "// {count} reviews",
    "work.reviewsCount": "{count} 件",
    "work.sortRating": "★ 評価順",
    "work.sortNew": "新着順",
    "work.sortHelpful": "参考になった順",
    "work.purchasedMark": "✓ 購入済",
    "work.sameAuthor": "{name} の他の作品",
    "work.sameAuthorEn": "// by same author",
    "work.similar": "似た作品",
    "work.similarEn": "// similar works",
    "work.info": "作品情報 // info",
    "work.tags": "タグ // tags",
    "work.author": "作者 // author",
    "work.notice": "注意 // notice",
    "work.infoId": "id",
    "work.infoGenre": "ジャンル",
    "work.infoPlayTime": "プレイ時間",
    "work.infoPlayTimeFmt": "約 {min} 分",
    "work.infoPrice": "価格",
    "work.infoAge": "年齢",
    "work.infoUpdated": "最終更新",
    "work.infoPublished": "公開日",
    "work.infoPlays": "プレイ数",
    "work.infoFavorites": "お気に入り",
    "work.authorWorks": "{count} 作品",
    "work.profileBtn": "プロフィール",
    "work.followBtn": "＋フォロー",
    "work.noticeBody": "プレイは play.pixadv.net で開きます。\nセーブデータは Pix アカウントに保存されます。",
    "work.noticeRefund": "返金については",
    "work.noticeRefundHere": "こちら",
    "work.engineStarting": "play.pixadv.net でゲームを起動中...",
    // 作者ページ
    "author.openDashboard": "ダッシュボードを開く",
    "author.following": "✓ フォロー中",
    "author.notFollowing": "＋ フォロー",
    "author.followers": "フォロワー {count} 人",
    "author.statsWorkCount": "作品数",
    "author.statsPublishedDraft": "公開 {pub} / 下書き {draft}",
    "author.totalPlays": "累計プレイ",
    "author.totalSales": "累計売上",
    "author.weeklyDelta": "▲ {n} (今週)",
    "author.monthlyDelta": "▲ ¥{n} (今月)",
    "author.tabWorks": "作品",
    "author.tabPopular": "人気順",
    "author.tabNew": "新着順",
    "author.tabDraft": "下書き",
    "author.gridView": "表示: グリッド ▾",
    "author.noWorks": "まだ作品がありません",
    "author.noWorksEn": "// no works yet",
    "author.followingHeading": "フォロー中の作者",
    "author.followingEn": "// following",
    "author.workCount": "{count}作品",
    "author.joined": "参加 {date}",
    // 検索
    "search.heading.noQuery": "作品を探す",
    "search.heading.query": "「{query}」 の検索結果",
    "search.heading.genre": "{genre} 作品",
    "search.heading.tag": "#{tag} 作品",
    "search.filterGenre": "ジャンル // genre",
    "search.filterPrice": "価格 // price",
    "search.filterAge": "年齢 // age",
    "search.filterTags": "タグ // tag-cloud",
    "search.selected": "選択中",
    "search.clearAll": "すべてクリア",
    "search.priceFree": "無料",
    "search.priceP1": "¥1 〜 ¥500",
    "search.priceP2": "¥501 〜 ¥1,000",
    "search.priceP3": "¥1,001 以上",
    "search.ageAll": "全年齢",
    "search.ageR15": "R-15",
    "search.ageR18": "R-18",
    "search.resultsCount": "{count} 件",
    "search.resultsTotal": "/ 計 {total} 作品中",
    "search.sortPopular": "人気",
    "search.sortNew": "新着",
    "search.sortRating": "★ 評価",
    "search.sortPriceAsc": "価格↑",
    "search.loadMore": "─ スクロールでさらに読み込み ─",
    "search.empty": "条件に合う作品が見つかりませんでした",
    "search.emptyDesc": "フィルタを減らしてみてください",
    // ライブラリ
    "library.title": "マイライブラリ",
    "library.summary": "/library · {total} 作品 · 進行中 {inprog} 件 · 未プレイ {unplayed} 件",
    "library.filterAll": "全部",
    "library.filterUnplayed": "未プレイ",
    "library.filterInProgress": "進行中",
    "library.filterCleared": "クリア済み",
    "library.statusCleared": "クリア済み",
    "library.statusInProgress": "進行中",
    "library.statusUnplayed": "未プレイ",
    "library.statusOwn": "✓ 自作",
    "library.progress": "進行度",
    "library.takenAt": "取得: {date}",
    "library.btnPlay": "プレイ",
    "library.btnResume": "再開",
    "library.btnReplay": "もう一度",
    "library.openInStudio": "Studio で開く",
    "library.empty": "このカテゴリにはまだ作品がありません",
    "library.emptyDesc": "// 作品を探してみましょう",
    "library.findWorks": "作品を探す →",
    "library.invitationTitle": "あなたも作品を書いてみませんか?",
    "library.invitationDesc": "ブラウザだけで完結する Studio で、今すぐ ADV を作れます。",
    // ダッシュボード
    "dash.creator": "クリエイター",
    "dash.plan": "plan: free",
    "dash.create": "制作",
    "dash.account": "アカウント",
    "dash.works": "作品",
    "dash.sales": "売上",
    "dash.stats": "統計",
    "dash.settings": "設定",
    "dash.openInStudio": "Studio で開く",
    "dash.publicProfile": "公開プロフィール",
    "dash.apiStatus": "API STATUS",
    "dash.worksSub": "/dashboard/works · 公開 {pub} / 下書き {draft} / 非公開 {priv}",
    "dash.salesSub": "/dashboard/sales · 過去 7 ヶ月の推移",
    "dash.statsSub": "/dashboard/stats · 全公開作品の数値を一覧で確認できます",
    "dash.settingsSub": "/dashboard/settings · 振込先・特定商取引法に基づく表記など",
    "dash.newWork": "＋ 新規作品を作る",
    "dash.studioNewWork": "studio.pixadv.net で新規プロジェクトを作成...",
    "dash.studioEdit": "studio.pixadv.net で編集...",
    "dash.studioMove": "studio.pixadv.net に移動...",
    "dash.monthlyPlays": "今月のプレイ",
    "dash.monthlySales": "今月の売上",
    "dash.newFollowers": "新規フォロワー",
    "dash.avgRating": "平均 ★ 評価",
    "dash.sameAsLast": "= 先月と同じ",
    "dash.filterAll": "全部",
    "dash.filterPublished": "公開",
    "dash.filterDraft": "下書き",
    "dash.filterPrivate": "非公開",
    "dash.sortUpdated": "並び: 更新日 ▾",
    "dash.statePublished": "公開中",
    "dash.stateDraft": "下書き",
    "dash.statePrivate": "非公開",
    "dash.colPlays": "PLAYS",
    "dash.colFavs": "FAVS",
    "dash.colRevenue": "REVENUE",
    "dash.btnEdit": "Studio で編集",
    "dash.btnView": "表示",
    "dash.statMonthlyEstimate": "今月見込み",
    "dash.statTotal": "累計売上 (税前)",
    "dash.statPast7": "過去 7 ヶ月",
    "dash.statPlatformFee": "プラットフォーム手数料",
    "dash.statStripeIncl": "// Stripe 手数料込み",
    "dash.statNextPayout": "次回振込予定",
    "dash.chartMonthly": "月別売上 // monthly revenue",
    "dash.payouts": "振込履歴 // payouts",
    "dash.payoutDate": "日付",
    "dash.payoutAmount": "金額",
    "dash.payoutStatus": "状態",
    "dash.payoutRef": "参照",
    "dash.payoutDone": "完了",
    "dash.stripeLinked": "連携済み",
    "dash.stripeActive": "● 有効",
    "dash.stripeFee": "手数料は売上の {pct}% です。次回振込は毎月 25 日に自動で行われます。",
    "dash.stripeOpen": "Stripe ダッシュボードを開く",
    "dash.statTotalPlays": "累計プレイ",
    "dash.statTotalFavs": "累計お気に入り",
    "dash.statReviewCount": "レビュー数",
    "dash.statAvgRating": "平均 ★ {n}",
    "dash.statClearRate": "完走率(平均)",
    "dash.statIndustryAvg": "= 業界平均: 42%",
    "dash.statWeeklyDelta": "▲ {n} (今週)",
    "dash.workByStats": "作品別 PV / プレイ / 購入数",
    "dash.last30d": "// last 30d",
    "dash.referrers": "流入元 // referrers",
    "dash.genreShare": "ジャンル別シェア",
    "dash.payout": "振込先 // payout",
    "dash.bankName": "銀行名",
    "dash.branchName": "支店名",
    "dash.acctType": "預金種別",
    "dash.acctNumber": "口座番号",
    "dash.acctHolder": "口座名義(カナ)",
    "dash.save": "保存",
    "dash.savedPayout": "振込先を更新しました",
    "dash.tokushoho": "特定商取引法に基づく表記 // tokushoho",
    "dash.tokushohoIntro": "有料作品を 1 件でも公開する場合、本表記の登録が必要です。",
    "dash.sellerName": "販売者名",
    "dash.address": "所在地",
    "dash.phone": "電話番号",
    "dash.contactEmail": "連絡先メール",
    "dash.savedGeneric": "保存しました",
    "dash.publishedNote": "● 公開済 · last sync {date}",
    // 認証
    "auth.tabLogin": "ログイン",
    "auth.tabSignup": "新規登録",
    "auth.lblDisplayName": "表示名 / display name",
    "auth.phDisplayName": "あなたの名前",
    "auth.lblEmail": "メール / email",
    "auth.lblPassword": "パスワード / password",
    "auth.errMissingEmail": "メールアドレスとパスワードを入力してください",
    "auth.errMissingName": "表示名を入力してください",
    "auth.remember": "ログイン状態を保つ",
    "auth.forgot": "パスワードを忘れた?",
    "auth.btnLogin": "ログイン →",
    "auth.btnSignup": "アカウントを作成 →",
    "auth.terms": "登録すると {terms} と {privacy} に同意したものとみなされます。",
    "auth.termsLink": "利用規約",
    "auth.privacyLink": "プライバシーポリシー",
    "auth.foot": "// JWT 互換 · pix-studio:token",
    "auth.backStudio": "← Studio へ戻る",
    "auth.toPortal": "Portal を見てみる →",
    "auth.demoLogin": "デモアカウントで入る →",
    "auth.welcome": "ようこそ、{name} さん",
    "auth.loggedOut": "ログアウトしました",
    "auth.orDivider": "または",
    "auth.googleErr": "Google ログイン失敗: {msg}",
    // フッター
    "foot.about": "About",
    "foot.legal": "Legal",
    "foot.help": "Help",
    "foot.studio": "Studio",
    "foot.aboutPix": "Pix とは",
    "foot.company": "運営会社",
    "foot.careers": "採用",
    "foot.press": "プレスキット",
    "foot.tos": "利用規約",
    "foot.privacy": "プライバシーポリシー",
    "foot.tokushoho": "特定商取引法に基づく表記",
    "foot.ageGuide": "年齢制限ガイドライン",
    "foot.contact": "お問い合わせ",
    "foot.faq": "クリエイター向けFAQ",
    "foot.status": "ステータス",
    "foot.discord": "Discord",
    "foot.apiDocs": "api docs",
    "foot.changelog": "変更履歴",
    "foot.tagline": "PIX STUDIO ONLINE — ADV PORTAL",
    "foot.copyright": "© 2026 Pix Studio Online — Made by creators, played by everyone.",
    "foot.langNote": "JP / EN",
    // 共通
    "common.loading": "読み込み中…",
    "common.error": "エラーが発生しました",
    "common.free": "無料",
    "common.ageAll": "全年齢",
    "common.ageR15": "R-15",
    "common.ageR18": "R-18",
    "common.langLabel": "Language",
    "common.langJa": "日本語",
    "common.langEn": "English",
    // 追加: レビュー投稿
    "review.writeTitle": "レビューを書く",
    "review.editTitle": "レビューを編集する",
    "review.rating": "あなたの評価",
    "review.body": "レビュー本文",
    "review.placeholder": "プレイした感想を 1〜4000 字で",
    "review.submit": "投稿する",
    "review.update": "更新する",
    "review.cancel": "キャンセル",
    "review.posted": "レビューを投稿しました",
    "review.requireLogin": "レビューにはログインが必要です",
    "review.loginNow": "ログインする →",
    "review.myMark": "あなたのレビュー",
    "review.editBtn": "編集",
    // PLAY
    "play.openingEngine": "play.pixadv.net で起動します...",
    "play.notPlayable": "この作品はまだプレイデータが紐付けられていません",
  },
  en: {
    "header.searchPlaceholder": "Search works, authors, tags",
    "header.searchAria": "Search",
    "header.genre": "Genre",
    "header.ranking": "Ranking",
    "header.library": "Library",
    "header.notifications": "Notifications",
    "header.notificationsTitle": "Notifications",
    "header.notificationsNewCount": "{count} new",
    "header.viewAll": "View all",
    "header.profile": "Profile",
    "header.creator": "Creator",
    "header.dashboard": "Author Dashboard",
    "header.openInStudio": "Open in Studio",
    "header.help": "Help",
    "header.logout": "Sign out",
    "header.login": "Sign in",
    "header.signup": "Sign up",
    "header.popularKeywords": "Popular keywords",
    "header.workSuggestions": "Suggestions",
    "mobile.home": "Home",
    "mobile.search": "Search",
    "mobile.library": "Library",
    "mobile.profile": "Profile",
    "home.pickup": "★ Editor's Pick · WEEK {week}",
    "home.playFree": "▶ Play Free",
    "home.viewDetail": "Details",
    "home.addLibrary": "Add to library",
    "home.new": "New",
    "home.newEn": "// new releases · 7d",
    "home.weeklyRanking": "Weekly Ranking",
    "home.weeklyRankingEn": "// weekly top 10",
    "home.byGenre": "Browse by Genre",
    "home.byGenreEn": "// by genre",
    "home.viewMore": "View more →",
    "home.viewAll": "All →",
    "home.reviews": "{count} reviews",
    "home.pickupSubtitle":
      "A quiet night, 7 choices, 3 endings. Free for a limited time.",
    "home.emptyGenre": "No works in this genre yet",
    "home.emptyGenreDesc": "Want to write one?",
    "home.studioCreate": "Create in Studio →",
    "work.home": "HOME",
    "work.r18Title": "R-18 content",
    "work.r18Note": "Click to reveal",
    "work.r18Show": "Reveal",
    "work.buy": "Buy",
    "work.play": "▶ Play",
    "work.share": "Share on X",
    "work.copyLink": "Copy link",
    "work.copyLinkDone": "Link copied",
    "work.purchaseDone": "Purchased “{title}” ({price})",
    "work.addLibraryDone": "Added to library",
    "work.removeLibraryDone": "Removed from library",
    "work.inLibrary": "♥ In library",
    "work.notInLibrary": "♡ Add to library",
    "work.screenshots": "SCREENSHOTS // {count}",
    "work.reviews": "Reviews",
    "work.reviewsEn": "// {count} reviews",
    "work.reviewsCount": "{count} reviews",
    "work.sortRating": "★ Rating",
    "work.sortNew": "Newest",
    "work.sortHelpful": "Helpful",
    "work.purchasedMark": "✓ Purchased",
    "work.sameAuthor": "More from {name}",
    "work.sameAuthorEn": "// by same author",
    "work.similar": "Similar works",
    "work.similarEn": "// similar works",
    "work.info": "Info",
    "work.tags": "Tags",
    "work.author": "Author",
    "work.notice": "Notice",
    "work.infoId": "id",
    "work.infoGenre": "Genre",
    "work.infoPlayTime": "Play time",
    "work.infoPlayTimeFmt": "~{min} min",
    "work.infoPrice": "Price",
    "work.infoAge": "Age",
    "work.infoUpdated": "Updated",
    "work.infoPublished": "Released",
    "work.infoPlays": "Plays",
    "work.infoFavorites": "Favorites",
    "work.authorWorks": "{count} works",
    "work.profileBtn": "Profile",
    "work.followBtn": "＋ Follow",
    "work.noticeBody":
      "Plays open on play.pixadv.net.\nSave data is stored in your Pix account.",
    "work.noticeRefund": "Refunds:",
    "work.noticeRefundHere": "here",
    "work.engineStarting": "Launching on play.pixadv.net…",
    "author.openDashboard": "Open dashboard",
    "author.following": "✓ Following",
    "author.notFollowing": "＋ Follow",
    "author.followers": "{count} followers",
    "author.statsWorkCount": "Works",
    "author.statsPublishedDraft": "Public {pub} / Drafts {draft}",
    "author.totalPlays": "Total plays",
    "author.totalSales": "Total revenue",
    "author.weeklyDelta": "▲ {n} (this week)",
    "author.monthlyDelta": "▲ ¥{n} (this month)",
    "author.tabWorks": "Works",
    "author.tabPopular": "Popular",
    "author.tabNew": "Newest",
    "author.tabDraft": "Drafts",
    "author.gridView": "Grid ▾",
    "author.noWorks": "No works yet",
    "author.noWorksEn": "// no works yet",
    "author.followingHeading": "Following",
    "author.followingEn": "// following",
    "author.workCount": "{count} works",
    "author.joined": "Joined {date}",
    "search.heading.noQuery": "Browse works",
    "search.heading.query": "Results for “{query}”",
    "search.heading.genre": "{genre} works",
    "search.heading.tag": "#{tag} works",
    "search.filterGenre": "Genre",
    "search.filterPrice": "Price",
    "search.filterAge": "Age",
    "search.filterTags": "Tags // tag-cloud",
    "search.selected": "Selected",
    "search.clearAll": "Clear all",
    "search.priceFree": "Free",
    "search.priceP1": "¥1 – 500",
    "search.priceP2": "¥501 – 1,000",
    "search.priceP3": "¥1,001+",
    "search.ageAll": "All ages",
    "search.ageR15": "R-15",
    "search.ageR18": "R-18",
    "search.resultsCount": "{count} results",
    "search.resultsTotal": "/ of {total} works",
    "search.sortPopular": "Popular",
    "search.sortNew": "New",
    "search.sortRating": "★ Rating",
    "search.sortPriceAsc": "Price ↑",
    "search.loadMore": "─ Scroll to load more ─",
    "search.empty": "No matching works",
    "search.emptyDesc": "Try removing some filters",
    "library.title": "My Library",
    "library.summary":
      "/library · {total} works · {inprog} in progress · {unplayed} unplayed",
    "library.filterAll": "All",
    "library.filterUnplayed": "Unplayed",
    "library.filterInProgress": "In progress",
    "library.filterCleared": "Cleared",
    "library.statusCleared": "Cleared",
    "library.statusInProgress": "In progress",
    "library.statusUnplayed": "Unplayed",
    "library.statusOwn": "✓ Own work",
    "library.progress": "Progress",
    "library.takenAt": "Added: {date}",
    "library.btnPlay": "Play",
    "library.btnResume": "Resume",
    "library.btnReplay": "Replay",
    "library.openInStudio": "Open in Studio",
    "library.empty": "Nothing in this category yet",
    "library.emptyDesc": "// try browsing works",
    "library.findWorks": "Browse works →",
    "library.invitationTitle": "Want to write your own?",
    "library.invitationDesc":
      "Studio runs in your browser — create an ADV right now.",
    "dash.creator": "Creator",
    "dash.plan": "plan: free",
    "dash.create": "Create",
    "dash.account": "Account",
    "dash.works": "Works",
    "dash.sales": "Sales",
    "dash.stats": "Stats",
    "dash.settings": "Settings",
    "dash.openInStudio": "Open in Studio",
    "dash.publicProfile": "Public profile",
    "dash.apiStatus": "API STATUS",
    "dash.worksSub":
      "/dashboard/works · Public {pub} / Drafts {draft} / Private {priv}",
    "dash.salesSub": "/dashboard/sales · last 7 months",
    "dash.statsSub": "/dashboard/stats · numbers across all public works",
    "dash.settingsSub":
      "/dashboard/settings · payout, statutory disclosures, etc",
    "dash.newWork": "＋ New work",
    "dash.studioNewWork": "Creating new project on studio.pixadv.net…",
    "dash.studioEdit": "Editing on studio.pixadv.net…",
    "dash.studioMove": "Going to studio.pixadv.net…",
    "dash.monthlyPlays": "Plays this month",
    "dash.monthlySales": "Sales this month",
    "dash.newFollowers": "New followers",
    "dash.avgRating": "Avg ★ rating",
    "dash.sameAsLast": "= same as last month",
    "dash.filterAll": "All",
    "dash.filterPublished": "Public",
    "dash.filterDraft": "Drafts",
    "dash.filterPrivate": "Private",
    "dash.sortUpdated": "Sort: updated ▾",
    "dash.statePublished": "Published",
    "dash.stateDraft": "Draft",
    "dash.statePrivate": "Private",
    "dash.colPlays": "PLAYS",
    "dash.colFavs": "FAVS",
    "dash.colRevenue": "REVENUE",
    "dash.btnEdit": "Edit in Studio",
    "dash.btnView": "View",
    "dash.statMonthlyEstimate": "This month (est.)",
    "dash.statTotal": "Total (pre-tax)",
    "dash.statPast7": "last 7 mo",
    "dash.statPlatformFee": "Platform fee",
    "dash.statStripeIncl": "// incl. Stripe",
    "dash.statNextPayout": "Next payout",
    "dash.chartMonthly": "Monthly revenue",
    "dash.payouts": "Payouts",
    "dash.payoutDate": "Date",
    "dash.payoutAmount": "Amount",
    "dash.payoutStatus": "Status",
    "dash.payoutRef": "Ref",
    "dash.payoutDone": "Done",
    "dash.stripeLinked": "Linked",
    "dash.stripeActive": "● Active",
    "dash.stripeFee":
      "Fee is {pct}% of sales. Payouts are automatic on the 25th.",
    "dash.stripeOpen": "Open Stripe dashboard",
    "dash.statTotalPlays": "Total plays",
    "dash.statTotalFavs": "Total favorites",
    "dash.statReviewCount": "Reviews",
    "dash.statAvgRating": "Avg ★ {n}",
    "dash.statClearRate": "Avg clear rate",
    "dash.statIndustryAvg": "= industry avg: 42%",
    "dash.statWeeklyDelta": "▲ {n} (this week)",
    "dash.workByStats": "Per-work PV / Plays / Sales",
    "dash.last30d": "// last 30d",
    "dash.referrers": "Referrers",
    "dash.genreShare": "Genre share",
    "dash.payout": "Payout account",
    "dash.bankName": "Bank",
    "dash.branchName": "Branch",
    "dash.acctType": "Type",
    "dash.acctNumber": "Account no.",
    "dash.acctHolder": "Account holder (Kana)",
    "dash.save": "Save",
    "dash.savedPayout": "Payout account updated",
    "dash.tokushoho": "Statutory disclosure",
    "dash.tokushohoIntro":
      "Required once you publish any paid work.",
    "dash.sellerName": "Seller name",
    "dash.address": "Address",
    "dash.phone": "Phone",
    "dash.contactEmail": "Contact email",
    "dash.savedGeneric": "Saved",
    "dash.publishedNote": "● Published · last sync {date}",
    "auth.tabLogin": "Sign in",
    "auth.tabSignup": "Sign up",
    "auth.lblDisplayName": "Display name",
    "auth.phDisplayName": "Your name",
    "auth.lblEmail": "Email",
    "auth.lblPassword": "Password",
    "auth.errMissingEmail": "Please enter email and password",
    "auth.errMissingName": "Please enter a display name",
    "auth.remember": "Remember me",
    "auth.forgot": "Forgot password?",
    "auth.btnLogin": "Sign in →",
    "auth.btnSignup": "Create account →",
    "auth.terms": "By signing up you agree to our {terms} and {privacy}.",
    "auth.termsLink": "Terms",
    "auth.privacyLink": "Privacy Policy",
    "auth.foot": "// JWT compat · pix-studio:token",
    "auth.backStudio": "← Back to Studio",
    "auth.toPortal": "Browse Portal →",
    "auth.demoLogin": "Continue as demo →",
    "auth.welcome": "Welcome back, {name}",
    "auth.loggedOut": "Signed out",
    "auth.orDivider": "or",
    "auth.googleErr": "Google sign-in failed: {msg}",
    "foot.about": "About",
    "foot.legal": "Legal",
    "foot.help": "Help",
    "foot.studio": "Studio",
    "foot.aboutPix": "What is Pix",
    "foot.company": "Company",
    "foot.careers": "Careers",
    "foot.press": "Press kit",
    "foot.tos": "Terms of Service",
    "foot.privacy": "Privacy Policy",
    "foot.tokushoho": "Statutory disclosure",
    "foot.ageGuide": "Age rating guide",
    "foot.contact": "Contact",
    "foot.faq": "Creator FAQ",
    "foot.status": "Status",
    "foot.discord": "Discord",
    "foot.apiDocs": "API docs",
    "foot.changelog": "Changelog",
    "foot.tagline": "PIX STUDIO ONLINE — ADV PORTAL",
    "foot.copyright":
      "© 2026 Pix Studio Online — Made by creators, played by everyone.",
    "foot.langNote": "JP / EN",
    "common.loading": "Loading…",
    "common.error": "Something went wrong",
    "common.free": "Free",
    "common.ageAll": "All ages",
    "common.ageR15": "R-15",
    "common.ageR18": "R-18",
    "common.langLabel": "Language",
    "common.langJa": "日本語",
    "common.langEn": "English",
    "review.writeTitle": "Write a review",
    "review.editTitle": "Edit your review",
    "review.rating": "Your rating",
    "review.body": "Review",
    "review.placeholder": "Share your thoughts (1–4000 chars)",
    "review.submit": "Post",
    "review.update": "Update",
    "review.cancel": "Cancel",
    "review.posted": "Review posted",
    "review.requireLogin": "Sign in to post a review",
    "review.loginNow": "Sign in →",
    "review.myMark": "Your review",
    "review.editBtn": "Edit",
    "play.openingEngine": "Launching on play.pixadv.net…",
    "play.notPlayable": "This work has no playable content yet",
  },
} as const;
